import { Component, effect, inject, OnInit, ViewChild } from '@angular/core';
import { Patient } from '../../calvin-emr-shared/models/patient.model';
import { PageComponent } from 'src/app/calvin-emr-shared/page/page.component';
import { ActivatedRoute, Router } from '@angular/router';
import { MarTabComponent } from 'src/app/patients/dashboard-page/mar-tab/mar-tab.component';
import {
  COLLECTION_PATIENTS,
  COLLECTION_STUDENT_PATIENTS,
  ROLE_ADMIN,
  ROLE_STUDENT,
} from '../../calvin-emr-shared/global';
import { MenuItem, PrimeTemplate } from 'primeng/api';
import { ProviderOrder } from 'src/app/calvin-emr-shared/models/provider-order.model';
import { PatientDashboardStore } from '../state/dashboard-page.store';
import { PATH_ID_PARAM, PATH_PATIENTS, PATH_ROLE_PARAM } from '../patients.global';
import { ToastService } from 'src/app/calvin-emr-shared/services/toast.service';
import { PATH_STUDENTS } from 'src/app/students/students.global';
import { AdminStore } from 'src/app/calvin-emr-shared/state/admin/admin.store';

import { ProgressBarModule } from 'primeng/progressbar';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { PatientSnapshotComponent } from './patient-snapshot/patient-snapshot.component';
import { TabsModule } from 'primeng/tabs';
import { AssessmentsTabComponent } from './assessments-tab/assessments-tab.component';
import { AssessmentsHistoryTabComponent } from './assessments-history-tab/assessments-history-tab.component';
import { VitalsTabComponent } from './vitals-tab/vitals-tab.component';
import { ProviderChartsTabComponent } from './provider-charts-tab/provider-charts-tab.component';
import { MarTabComponent as MarTabComponent_1 } from './mar-tab/mar-tab.component';
import { UploadTabComponent } from './upload-tab/upload-tab.component';
import { CarePlanTabComponent } from './care-plan-tab/care-plan-tab.component';
import { ERROR } from 'src/app/global';

@Component({
  selector: 'app-dashboard-page',
  templateUrl: './dashboard-page.component.html',
  styleUrls: ['./dashboard-page.component.scss'],
  providers: [PatientDashboardStore],
  standalone: true,
  imports: [
    ProgressBarModule,
    BreadcrumbModule,
    PatientSnapshotComponent,
    TabsModule,
    AssessmentsTabComponent,
    AssessmentsHistoryTabComponent,
    VitalsTabComponent,
    ProviderChartsTabComponent,
    MarTabComponent_1,
    UploadTabComponent,
    CarePlanTabComponent
],
})
export class PatientDashboardComponent extends PageComponent implements OnInit {
  breadcrumbItems: MenuItem[] = [];

  currentMainTabValue = '0';

  // Stores current patient information
  currentPatient = new Patient();
  parentPath: string = '';
  patientId: string | null = '';
  role: string | null = '';
  profilePicSrc = '';
  providerOrders: ProviderOrder[] = [];

  // Needed to repaint the MAR scheduler so that it loads correctly on the first open
  // and reloads each time thereafter
  @ViewChild(MarTabComponent, { static: false }) mar!: MarTabComponent;
  @ViewChild(VitalsTabComponent, { static: false }) vitalsTab?: VitalsTabComponent;

  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private readonly patientStore = inject(PatientDashboardStore);
  private toastService = inject(ToastService);
  private adminStore = inject(AdminStore);

  constructor() {
    super();
    this.processRoute();

    effect(() => {
      const patient = this.patientStore.patient();
      const student = this.adminStore.student();
      if (!patient.id) return; // Skip the initial empty patient

      this.currentPatient = patient;
      this.breadcrumbItems =
        this.parentPath === PATH_PATIENTS
          ? [{ label: 'Patient List', routerLink: `/${PATH_PATIENTS}` }]
          : [
              {
                label: `View Students - ${student?.firstNameLastName}`,
                routerLink: `/${PATH_STUDENTS}`,
              },
            ];

      this.breadcrumbItems = [
        ...this.breadcrumbItems,
        { label: `View Patient - ${this.currentPatient.getNameFirstMiddleLast()}` },
      ];
      this.stopLoading();
    });
  }

  ngOnInit(): void {
    super.ngOnInit();
  }

  onMainTabChange(nextTabValue: string | number | undefined): void {
    if (nextTabValue === undefined) return;
    const next = String(nextTabValue);

    if (this.currentMainTabValue === '1' && next !== '1') {
      this.vitalsTab?.flushPendingVitals('tab-switch');
    }

    this.currentMainTabValue = next;
  }

  private processRoute() {
    // Get the top-level route (either 'patients' or 'students')
    const topPath = this.route.parent?.snapshot.url[0].path;
    let col;

    // ID parameter will be the same for both types
    this.patientId = this.route.snapshot.paramMap.get(PATH_ID_PARAM);

    if (topPath) {
      this.parentPath = topPath;
      switch (this.parentPath) {
        case PATH_PATIENTS: {
          // Get id from the router URL, set from PatientList component
          this.role = this.route.snapshot.paramMap.get(PATH_ROLE_PARAM);
          col = this.role === ROLE_ADMIN ? COLLECTION_PATIENTS : COLLECTION_STUDENT_PATIENTS;
          break;
        }
        case PATH_STUDENTS: {
          this.role = 'admin';
          col = 'studentPatients';
          if (this.patientId) {
            this.adminStore.setStudent(this.patientId.split('-')[0]);
          }
          break;
        }
        default:
          this.role = 'student';
          col = 'studentPatients';
      }

      if (!this.patientId || !this.isRoleValid) {
        this.toastService.clearAndAddOne({
          severity: ERROR,
          summary: 'Error',
          detail: `Unable to load patient data!`,
        });
      } else {
        this.patientStore.getPatient({
          patientId: this.patientId,
          collection: col,
        });
      }
    }
  }

  get isPatientIdValid(): boolean {
    return this.patientId !== undefined && this.role !== null;
  }

  get isRoleValid(): boolean {
    return (
      this.role !== undefined &&
      this.role !== null &&
      (this.role === ROLE_ADMIN || this.role === ROLE_STUDENT)
    );
  }
}
